import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { Typography } from "@mui/material";
import { debounce } from "@mui/material/utils";

import { useMapsContext } from "~/providers/MapsProvider";
import FilterAutocomplete, { FilterAutocompleteParams } from "./FilterAutocomplete";

export interface AddressOption {
  id: string;
  value: string;
  label: string;
  origData: any;
}

interface AddressPickerProps extends Omit<FilterAutocompleteParams, "options" | "filterKey" | "setFilters" | "filters"> {
  filterKey?: FilterAutocompleteParams["filterKey"];
  setFilters?: FilterAutocompleteParams["setFilters"];
  filters?: FilterAutocompleteParams["filters"];
  onAddressPicked?: (option?: AddressOption, geoFeature?: any) => void;
  onValueChange?: (value: string) => void;
  value?: string;
  error?: boolean;
  helperText?: string;
}

const AddressPicker: FC<AddressPickerProps> = ({ onAddressPicked, onValueChange, error, helperText, value, ...rest }) => {
  const { filterKey, setFilters } = rest;

  const { queryForAddress, getCoordinates } = useMapsContext();

  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value ?? "");

  const filtersValue = value ? { value } : null;

  const handleSelection = async (_event: any, option: AddressOption | null) => {
    if (!option) {
      setInputValue("");
      setFilters?.({ key: filterKey!, value: null });
      onAddressPicked?.();
      onValueChange?.("");
      return;
    }

    setInputValue(option.value);
    onValueChange?.(option.value);

    if (onAddressPicked) {
      try {
        const data = await getCoordinates(option.value);
        onAddressPicked(option, data.features?.[0]);
      } catch {
        alert("An error occurred while trying to locate this address. Please try again.");
      }
    }

    setFilters?.({ key: filterKey!, value: option });
  };

  const fetchAddresses = useMemo(
    () =>
      debounce(async (query: string) => {
        if (query.length < 3) {
          setAddressOptions([]);
          return;
        }

        setIsLoading(true);
        try {
          const queryResponse = await queryForAddress(query);
          const formatted: AddressOption[] = queryResponse.features.map((feature: any, index: number) => {
            const props = feature.properties;
            const formattedAddress = props.address?.formattedAddress;
            const name = props.name;

            const label = [name, formattedAddress].filter(Boolean).join(", ");
            const value = formattedAddress ?? label;

            return {
              id: feature.id || `${index}-${value}`,
              value,
              label,
              origData: feature,
            };
          });

          setAddressOptions(formatted);
        } catch {
          setAddressOptions([]);
        } finally {
          setIsLoading(false);
        }
      }, 400),
    [queryForAddress],
  );

  const handleInputChange = useCallback(
    (_event: any, newInputValue: string, reason: string) => {
      if (reason === "reset") {
        return;
      }

      setInputValue(newInputValue);
      if (newInputValue.length >= 3) {
        setIsLoading(true);
      } else {
        setIsLoading(false);
        setAddressOptions([]);
      }
      fetchAddresses(newInputValue);
    },
    [fetchAddresses],
  );

  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  return (
    <>
      <FilterAutocomplete
        {...(rest as FilterAutocompleteParams)}
        filterKey={filterKey ?? ""}
        filterOptions={(options: any) => options}
        filters={rest.filters ?? { [filterKey ?? ""]: filtersValue }}
        inputValue={inputValue}
        isLoading={isLoading}
        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
        multiple={false}
        onChange={handleSelection}
        onInputChange={handleInputChange}
        options={addressOptions}
        sx={{
          ...(error && {
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "error.main",
              },
            },
          }),
          ...rest.sx,
        }}
        noAutoselectAll
        noTooltip
      />
      {helperText && (
        <Typography color={error ? "error" : "text.secondary"} sx={{ mt: 0.5, mx: 1.75 }} variant="caption">
          {helperText}
        </Typography>
      )}
    </>
  );
};

export default AddressPicker;
